import { useState } from 'react';
import { uploadCostImages } from '../api/costImages';

interface UseUploadCostImagesResult {
  uploadImages: (files: File[]) => Promise<void>;
  isUploading: boolean;
  error: string | null;
}

/**
 * Hook to handle cost image uploads
 * - Manages upload state
 * - Handles errors
 * - Calls success callback when upload completes
 */
export function useUploadCostImages(
  jobId: number,
  costId: number,
  onSuccess?: () => void
): UseUploadCostImagesResult {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadImages = async (files: File[]) => {
    if (files.length === 0) return;

    setIsUploading(true);
    setError(null);

    try {
      await uploadCostImages(jobId, costId, files);

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const errorMessage = 'Failed to upload images';
      setError(errorMessage);
      console.error('Error uploading cost images:', err);
      throw new Error(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  return {
    uploadImages,
    isUploading,
    error,
  };
}
